import { useEffect, useState } from 'react';
import { Button, Input } from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { Alert } from '@vnetwork/ui/feedback';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@vnetwork/ui/primitives';
import {
  useCreateCustomerMailer,
  useUpdateCustomerMailer,
  type CustomerMailerRow,
  type CustomerMailerPayload,
} from '@/api/customer-mailers';

/** Extract a human-readable message from an axios/unknown error. */
function errorMessage(err: unknown): string {
  const res = (err as { response?: { data?: { error_description?: string; error?: string } } })
    ?.response;
  return res?.data?.error_description ?? res?.data?.error ?? 'Something went wrong.';
}

interface MailerFormDialogProps {
  customerId: number;
  /** Pass an existing mailer to switch to edit mode; omit for create. */
  mailer?: CustomerMailerRow;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Create / Edit Mailer dialog — a single Dialog form for both modes.
 * Fields: Mailer name, Recipients (comma-separated), CC, BCC.
 * Wires POST /customers/:id/mailers (create) and PUT /customers/:id/mailers/:mid (edit).
 * USER submits the live mutation; assistant builds UI only.
 */
export function MailerFormDialog({ customerId, mailer, open, onOpenChange }: MailerFormDialogProps) {
  const isEdit = !!mailer;

  const [name, setName] = useState('');
  const [receiver, setReceiver] = useState('');
  const [cc, setCc] = useState('');
  const [bcc, setBcc] = useState('');

  // Populate fields from existing mailer when opening in edit mode
  useEffect(() => {
    if (open) {
      setName(mailer?.customer_mailer_name ?? '');
      setReceiver(mailer?.mailer_receiver ?? '');
      setCc(mailer?.mailer_cc ?? '');
      setBcc(mailer?.mailer_bcc ?? '');
    }
  }, [open, mailer]);

  const create = useCreateCustomerMailer(customerId);
  const update = useUpdateCustomerMailer(customerId);
  const mutation = isEdit ? update : create;

  const reset = () => {
    setName(''); setReceiver(''); setCc(''); setBcc('');
    create.reset(); update.reset();
  };

  const handleClose = (next: boolean) => {
    if (!mutation.isPending) { onOpenChange(next); if (!next) reset(); }
  };

  const submit = () => {
    if (!name.trim() || !receiver.trim()) return;
    const payload: CustomerMailerPayload = {
      customer_mailer_name: name.trim(),
      mailer_type_ids: mailer?.mailer_type_ids ?? [],
      mailer_receiver: receiver.trim(),
      mailer_cc: cc.trim() || undefined,
      mailer_bcc: bcc.trim() || undefined,
      service_ids: mailer?.service_ids ?? [],
    };

    if (isEdit && mailer) {
      (update as ReturnType<typeof useUpdateCustomerMailer>).mutate(
        { mailerId: mailer.customer_mailer_id, payload },
        { onSuccess: () => { onOpenChange(false); reset(); } },
      );
    } else {
      (create as ReturnType<typeof useCreateCustomerMailer>).mutate(payload, {
        onSuccess: () => { onOpenChange(false); reset(); },
      });
    }
  };

  const isPending = mutation.isPending;
  const isError = mutation.isError;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent style={{ maxWidth: 480 }}>
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Mailer' : 'Create Mailer'}</DialogTitle>
        </DialogHeader>

        <div style={{ display: 'grid', gap: 'var(--vn-space-3)', padding: 'var(--vn-space-1) 0' }}>
          {isError ? (
            <Alert tone="danger" title={isEdit ? 'Could not update mailer' : 'Could not create mailer'}>
              {errorMessage(mutation.error)}
            </Alert>
          ) : null}

          <Field label="Mailer name" required>
            {(p) => (
              <Input
                {...p}
                placeholder="e.g. Invoice notifications"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            )}
          </Field>

          <Field label="Recipients (To)" required>
            {(p) => (
              <Input
                {...p}
                placeholder="email1@example.com, email2@example.com"
                value={receiver}
                onChange={(e) => setReceiver(e.target.value)}
              />
            )}
          </Field>

          <Field label="CC">
            {(p) => (
              <Input
                {...p}
                placeholder="Optional CC addresses"
                value={cc}
                onChange={(e) => setCc(e.target.value)}
              />
            )}
          </Field>

          <Field label="BCC">
            {(p) => (
              <Input
                {...p}
                placeholder="Optional BCC addresses"
                value={bcc}
                onChange={(e) => setBcc(e.target.value)}
              />
            )}
          </Field>
        </div>

        <DialogFooter>
          <Button variant="secondary" onClick={() => handleClose(false)} disabled={isPending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={isPending}
            disabled={!name.trim() || !receiver.trim()}
            onClick={submit}
          >
            {isEdit ? 'Save changes' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
